import { Money } from '../ui/Money';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { Wallet, MinusCircle, CheckCircle2, Plus, Trash2, Loader2, Users, Contact, HeartHandshake, FileSpreadsheet, FileText, Archive, History, CalendarDays, Save, Download } from 'lucide-react';
import { SendReportButton } from '../common/SendReportButton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const aedText = (v) => `AED ${Math.round(v || 0).toLocaleString()}`;
const aed = (v) => <Money value={v} />;

const InsuranceCtcSummary = () => {
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', amount: '', date: '', note: '' });
  const [saving, setSaving] = useState(false);

  // Policy year + archives
  const [policy, setPolicy] = useState({ period_label: '', start_date: '', end_date: '' });
  const [policySaving, setPolicySaving] = useState(false);
  const [archives, setArchives] = useState([]);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [expandedArchive, setExpandedArchive] = useState(null);
  const [archiveForm, setArchiveForm] = useState({ new_period_label: '', new_start_date: '', new_end_date: '' });

  const loadArchives = useCallback(() => {
    axios.get(`${API}/api/insurance/archives`, { headers })
      .then(r => setArchives(r.data?.archives || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/insurance/ctc-summary`, { headers })
      .then(r => { setData(r.data); setPolicy(r.data?.policy || { period_label: '', start_date: '', end_date: '' }); })
      .catch(() => toast({ title: 'Failed to load insurance CTC', variant: 'destructive' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); loadArchives(); }, [load, loadArchives]);

  const savePolicy = async () => {
    setPolicySaving(true);
    try {
      await axios.put(`${API}/api/insurance/policy`, policy, { headers });
      toast({ title: 'Policy year saved' });
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setPolicySaving(false); }
  };

  const doArchive = async () => {
    if (!policy.period_label?.trim()) {
      toast({ title: 'Set the current Policy Year first', description: 'e.g. 2025-26', variant: 'destructive' }); return;
    }
    setArchiving(true);
    try {
      await axios.post(`${API}/api/insurance/archive`, {
        period_label: policy.period_label.trim(),
        start_date: policy.start_date || null,
        end_date: policy.end_date || null,
        clear_refunds: true,
        new_period_label: archiveForm.new_period_label || null,
        new_start_date: archiveForm.new_start_date || null,
        new_end_date: archiveForm.new_end_date || null,
      }, { headers });
      toast({ title: `${policy.period_label} archived`, description: 'Refunds reset — ready for the new year.' });
      setArchiveOpen(false);
      setArchiveForm({ new_period_label: '', new_start_date: '', new_end_date: '' });
      load(); loadArchives();
    } catch (e) {
      toast({ title: 'Archive failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setArchiving(false); }
  };

  const deleteArchive = async (id) => {
    if (!window.confirm('Delete this archived year permanently?')) return;
    try {
      await axios.delete(`${API}/api/insurance/archives/${id}`, { headers });
      loadArchives();
    } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const downloadArchive = (id, label, format) => {
    const token2 = localStorage.getItem('token');
    const url = `${API}/api/insurance/archives/${id}/download?format=${format}`;
    axios.get(url, { headers: token2 ? { Authorization: `Bearer ${token2}` } : {}, responseType: 'blob' })
      .then(r => {
        const u = URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement('a');
        a.href = u; a.download = `Insurance_CTC_${(label || 'year').replace(/[/ ]/g, '-')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
      })
      .catch(() => toast({ title: 'Download failed', variant: 'destructive' }));
  };

  const addRefund = async () => {
    if (!form.name.trim() || form.amount === '' || isNaN(parseFloat(form.amount))) {
      toast({ title: 'Enter a name and amount', variant: 'destructive' }); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/insurance/refunds`, {
        name: form.name.trim(), amount: parseFloat(form.amount),
        date: form.date || null, note: form.note || null,
      }, { headers });
      setForm({ name: '', amount: '', date: '', note: '' });
      toast({ title: 'Refund added' });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const removeRefund = async (id) => {
    try {
      await axios.delete(`${API}/api/insurance/refunds/${id}`, { headers });
      load();
    } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const [downloading, setDownloading] = useState(null);
  const download = async (format) => {
    setDownloading(format);
    try {
      const r = await axios.get(`${API}/api/insurance/ctc-summary/download?format=${format}`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Insurance_CTC.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' });
    } finally { setDownloading(null); }
  };

  if (loading) {
    return <Card><CardContent className="p-6 flex items-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading insurance CTC…</CardContent></Card>;
  }
  if (!data) return null;

  return (
    <Card data-testid="insurance-ctc-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-emerald-600" />
          <h2 className="text-lg font-bold text-gray-900">Insurance CTC</h2>
          <span className="text-xs text-gray-500">· annual cost to company</span>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => download('excel')} disabled={!!downloading}
              className="h-8 gap-1.5 text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="ctc-export-excel">
              {downloading === 'excel' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />} Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => download('pdf')} disabled={!!downloading}
              className="h-8 gap-1.5 text-rose-700 border-rose-300 hover:bg-rose-50" data-testid="ctc-export-pdf">
              {downloading === 'pdf' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} PDF
            </Button>
            <SendReportButton
              reportUrl="/api/insurance/ctc-summary/download?format=pdf"
              filename="Insurance_CTC.pdf"
              defaultSubject="Insurance CTC — Annual Cost to Company"
              className="h-8 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              testid="ctc-email"
            />
          </div>
        </div>

        {/* Policy year bar */}
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 flex flex-wrap items-end gap-3" data-testid="policy-year-bar">
          <div className="flex items-center gap-1.5 text-indigo-700 font-semibold text-sm mr-1">
            <CalendarDays className="h-4 w-4" /> Policy Year
          </div>
          <div>
            <Label className="text-[11px] text-indigo-700">Period label</Label>
            <Input value={policy.period_label || ''} onChange={e => setPolicy(p => ({ ...p, period_label: e.target.value }))}
              placeholder="e.g. 2025-26" className="h-9 w-32" data-testid="policy-period-label" />
          </div>
          <div>
            <Label className="text-[11px] text-indigo-700">Start</Label>
            <Input type="date" value={policy.start_date || ''} onChange={e => setPolicy(p => ({ ...p, start_date: e.target.value }))}
              className="h-9" data-testid="policy-start-date" />
          </div>
          <div>
            <Label className="text-[11px] text-indigo-700">End (expiry)</Label>
            <Input type="date" value={policy.end_date || ''} onChange={e => setPolicy(p => ({ ...p, end_date: e.target.value }))}
              className="h-9" data-testid="policy-end-date" />
          </div>
          <Button size="sm" variant="outline" onClick={savePolicy} disabled={policySaving}
            className="h-9 border-indigo-300 text-indigo-700" data-testid="policy-save">
            {policySaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save
          </Button>
          <Button size="sm" onClick={() => setArchiveOpen(true)}
            className="h-9 ml-auto bg-indigo-600 hover:bg-indigo-700" data-testid="archive-start-new-year">
            <Archive className="h-4 w-4 mr-1" /> Archive &amp; Start New Year
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-xl border bg-blue-50 border-blue-200 p-4" data-testid="ctc-total">
            <p className="text-xs text-blue-700 font-medium flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> Total Insurance CTC</p>
            <p className="text-2xl font-bold text-blue-800 mt-1">{aed(data.total_ctc)}</p>
            <p className="text-[11px] text-blue-600 mt-1">Staff + dependents + visa holders</p>
          </div>
          <div className="rounded-xl border bg-rose-50 border-rose-200 p-4" data-testid="ctc-refunds">
            <p className="text-xs text-rose-700 font-medium flex items-center gap-1"><MinusCircle className="h-3.5 w-3.5" /> (−) Premium Refunds</p>
            <p className="text-2xl font-bold text-rose-700 mt-1">− {aed(data.refunds_total)}</p>
            <p className="text-[11px] text-rose-500 mt-1">Returned when staff leave / cancelled</p>
          </div>
          <div className="rounded-xl border bg-emerald-50 border-emerald-300 p-4" data-testid="ctc-actual">
            <p className="text-xs text-emerald-700 font-medium flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> = Actual Insurance CTC</p>
            <p className="text-2xl font-bold text-emerald-800 mt-1">{aed(data.actual_ctc)}</p>
            <p className="text-[11px] text-emerald-600 mt-1">Total − refunds</p>
          </div>
        </div>

        {/* Breakdown chips */}
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-3 py-1"><Users className="h-3.5 w-3.5 text-gray-500" /> Employees: <strong>{aed(data.employee_total)}</strong></span>
          <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-3 py-1"><HeartHandshake className="h-3.5 w-3.5 text-gray-500" /> Dependents: <strong>{aed(data.dependent_total)}</strong></span>
          <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-3 py-1"><Contact className="h-3.5 w-3.5 text-gray-500" /> Visa holders: <strong>{aed(data.visa_holder_total)}</strong></span>
        </div>

        {/* Refunds itemized */}
        <div className="border rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">Premium Refunds</div>
          <div className="divide-y">
            {(data.refunds || []).length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-400">No refunds recorded yet.</p>
            ) : (data.refunds || []).map(r => (
              <div key={r.id} className="px-4 py-2 flex items-center justify-between text-sm" data-testid={`refund-row-${r.id}`}>
                <div>
                  <span className="font-medium text-gray-800">{r.name}</span>
                  {r.date && <span className="text-gray-400 text-xs ml-2">{r.date}</span>}
                  {r.note && <span className="text-gray-500 text-xs ml-2">· {r.note}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono font-semibold text-rose-600">− {aed(r.amount)}</span>
                  <button onClick={() => removeRefund(r.id)} className="text-gray-400 hover:text-red-600" data-testid={`refund-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
          {/* Add refund */}
          <div className="bg-gray-50/60 px-4 py-3 grid grid-cols-1 sm:grid-cols-[1.4fr_0.8fr_0.9fr_1.4fr_auto] gap-2 items-end">
            <div><Label className="text-[11px]">Staff name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Who left / cancelled" className="h-9" data-testid="refund-name" /></div>
            <div><Label className="text-[11px]">Amount</Label>
              <Input type="number" min={0} value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" className="h-9" data-testid="refund-amount" /></div>
            <div><Label className="text-[11px]">Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="h-9" data-testid="refund-date" /></div>
            <div><Label className="text-[11px]">Note (optional)</Label>
              <Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. visa cancelled" className="h-9" data-testid="refund-note" /></div>
            <Button onClick={addRefund} disabled={saving} className="bg-rose-600 hover:bg-rose-700 h-9 gap-1" data-testid="refund-add">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
            </Button>
          </div>
        </div>

        {/* Previous policy years */}
        <div className="border rounded-xl overflow-hidden" data-testid="archives-section">
          <div className="bg-indigo-50 px-4 py-2 text-xs font-semibold text-indigo-700 uppercase tracking-wider flex items-center gap-1.5">
            <History className="h-3.5 w-3.5" /> Previous Policy Years ({archives.length})
          </div>
          {archives.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-400">No archived years yet. Click "Archive &amp; Start New Year" when a policy expires.</p>
          ) : (
            <div className="divide-y">
              {archives.map(a => (
                <div key={a.id} data-testid={`archive-row-${a.id}`}>
                  <div className="px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1">
                    <div className="font-semibold text-gray-900 min-w-[90px]">{a.period_label}</div>
                    <div className="text-xs text-gray-500 flex-1 min-w-[140px]">
                      {(a.start_date || a.end_date) ? `${a.start_date || '—'} → ${a.end_date || '—'}` : 'No dates'}
                    </div>
                    <div className="text-sm"><span className="text-gray-400 text-xs">Total CTC </span><span className="font-mono font-semibold text-blue-700">{aed(a.total_ctc)}</span></div>
                    <div className="text-sm"><span className="text-gray-400 text-xs">Saved / Refunded </span><span className="font-mono font-semibold text-rose-600">{aed(a.refunds_total)}</span></div>
                    <div className="text-sm"><span className="text-gray-400 text-xs">Actual </span><span className="font-mono font-semibold text-emerald-700">{aed(a.actual_ctc)}</span></div>
                    <div className="flex items-center gap-1 ml-auto">
                      <Button size="sm" variant="ghost" className="h-7 text-gray-600" onClick={() => setExpandedArchive(expandedArchive === a.id ? null : a.id)} data-testid={`archive-toggle-${a.id}`}>
                        {expandedArchive === a.id ? 'Hide' : `Refunds (${a.refund_count || 0})`}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-emerald-700" onClick={() => downloadArchive(a.id, a.period_label, 'excel')} data-testid={`archive-excel-${a.id}`}><FileSpreadsheet className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 text-rose-700" onClick={() => downloadArchive(a.id, a.period_label, 'pdf')} data-testid={`archive-pdf-${a.id}`}><Download className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 text-gray-400 hover:text-red-600" onClick={() => deleteArchive(a.id)} data-testid={`archive-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                  {expandedArchive === a.id && (
                    <div className="px-4 pb-3 bg-gray-50/60">
                      {(a.refunds || []).length === 0 ? (
                        <p className="text-xs text-gray-400 py-2">No refunds in this year.</p>
                      ) : (
                        <div className="divide-y border rounded-lg bg-white">
                          {(a.refunds || []).map((r, i) => (
                            <div key={r.id || i} className="px-3 py-1.5 flex items-center justify-between text-xs">
                              <span className="text-gray-700">{r.name}{r.date ? <span className="text-gray-400 ml-2">{r.date}</span> : null}{r.note ? <span className="text-gray-500 ml-2">· {r.note}</span> : null}</span>
                              <span className="font-mono font-semibold text-rose-600">− {aed(r.amount)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>

      {/* Archive & start new year dialog */}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent data-testid="archive-dialog">
          <DialogHeader>
            <DialogTitle>Archive {policy.period_label || 'current year'} &amp; start new year</DialogTitle>
            <DialogDescription>
              This saves a permanent snapshot of the current insurance CTC for <b>{policy.period_label || '(set a label first)'}</b>, then clears the premium refunds so the new year starts fresh.
            </DialogDescription>
          </DialogHeader>
          {data && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-2"><p className="text-[10px] text-blue-600">Total CTC</p><p className="font-bold text-blue-800 text-sm">{aed(data.total_ctc)}</p></div>
              <div className="rounded-lg bg-rose-50 border border-rose-200 p-2"><p className="text-[10px] text-rose-600">Saved / Refunded</p><p className="font-bold text-rose-700 text-sm">{aed(data.refunds_total)}</p></div>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2"><p className="text-[10px] text-emerald-600">Actual CTC</p><p className="font-bold text-emerald-800 text-sm">{aed(data.actual_ctc)}</p></div>
            </div>
          )}
          <div className="border-t pt-3 space-y-2">
            <p className="text-xs font-semibold text-gray-600">New policy year (optional — you can also set it later)</p>
            <div className="flex flex-wrap gap-2">
              <div><Label className="text-[11px]">Period label</Label>
                <Input value={archiveForm.new_period_label} onChange={e => setArchiveForm(f => ({ ...f, new_period_label: e.target.value }))} placeholder="e.g. 2026-27" className="h-9 w-32" data-testid="archive-new-label" /></div>
              <div><Label className="text-[11px]">Start</Label>
                <Input type="date" value={archiveForm.new_start_date} onChange={e => setArchiveForm(f => ({ ...f, new_start_date: e.target.value }))} className="h-9" data-testid="archive-new-start" /></div>
              <div><Label className="text-[11px]">End (expiry)</Label>
                <Input type="date" value={archiveForm.new_end_date} onChange={e => setArchiveForm(f => ({ ...f, new_end_date: e.target.value }))} className="h-9" data-testid="archive-new-end" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)} data-testid="archive-cancel">Cancel</Button>
            <Button onClick={doArchive} disabled={archiving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="archive-confirm">
              {archiving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Archive className="h-4 w-4 mr-1" />} Archive &amp; Start New Year
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InsuranceCtcSummary;
